<template>
  <div class="dash-page">
    <header class="dash-header">
      <router-link class="dash-brand" :to="homePath">
        <img src="/brain-icon-96.jpg" alt="" width="28" height="28" />
        <span>Remote EMDR</span>
      </router-link>
      <div class="dash-header-right">
        <span class="dash-email">{{ email }}</span>
        <button class="dash-logout" @click="doLogout">{{ $t('auth.logout') }}</button>
      </div>
    </header>

    <main class="dash-main">
      <h1>{{ $t('dashboard.title') }}</h1>
      <p class="dash-sub">{{ $t('dashboard.subtitle') }}</p>

      <!-- Add patient -->
      <form class="dash-add" @submit.prevent="addPatient">
        <input
          v-model="newPatientName"
          class="dash-input"
          :placeholder="$t('dashboard.newPatientPlaceholder')"
          maxlength="200"
          required />
        <button class="dash-btn-primary" type="submit" :disabled="busy">
          {{ $t('dashboard.addPatient') }}
        </button>
      </form>

      <p v-if="errorMessage" class="dash-error">{{ errorMessage }}</p>
      <p v-if="!patients.length && loaded" class="dash-empty">{{ $t('dashboard.empty') }}</p>

      <!-- Patient list -->
      <ul class="patient-list">
        <li v-for="patient in patients" :key="patient.id" class="patient-card">
          <div class="patient-row">
            <button class="patient-name" @click="toggleExpand(patient.id)">
              <span class="patient-caret">{{ expandedId === patient.id ? '▾' : '▸' }}</span>
              {{ patient.name }}
              <span class="patient-meta">
                {{ patient.session_count }} {{ $tc('dashboard.sessionsLabel', patient.session_count) }} ·
                {{ patient.note_count }} {{ $tc('dashboard.notesLabel', patient.note_count) }}
              </span>
            </button>
            <div class="patient-actions">
              <button class="dash-btn-primary dash-btn-small" @click="startSession(patient)">
                {{ $t('dashboard.startSession') }}
              </button>
              <button class="dash-btn-danger" @click="deletePatient(patient)">
                {{ $t('dashboard.delete') }}
              </button>
            </div>
          </div>

          <!-- Dossier -->
          <div v-if="expandedId === patient.id" class="dossier">
            <div class="dossier-col">
              <h3>{{ $t('dashboard.dossierNotes') }}</h3>
              <form @submit.prevent="addNote(patient)">
                <textarea
                  v-model="newNote"
                  class="dash-textarea"
                  rows="3"
                  maxlength="10000"
                  :placeholder="$t('dashboard.notePlaceholder')"
                  required></textarea>
                <button class="dash-btn-primary dash-btn-small" type="submit" :disabled="busy">
                  {{ $t('dashboard.addNote') }}
                </button>
              </form>
              <ul class="note-list">
                <li v-for="note in notes" :key="note.id" class="note-item">
                  <div class="note-head">
                    <span class="note-date">{{ formatDate(note.created) }}</span>
                    <button class="note-delete" @click="deleteNote(patient, note)">✕</button>
                  </div>
                  <p class="note-text">{{ note.note }}</p>
                </li>
              </ul>
              <p v-if="!notes.length" class="dash-empty-small">{{ $t('dashboard.noNotes') }}</p>
            </div>
            <div class="dossier-col">
              <h3>{{ $t('dashboard.sessionHistory') }}</h3>
              <ul class="session-list">
                <li v-for="session in sessions" :key="session.id" class="session-item">
                  <span>{{ formatDate(session.started) }}</span>
                  <a :href="controllerUrl(session.session_id)" target="_blank" rel="noopener">
                    {{ $t('dashboard.openSession') }}
                  </a>
                </li>
              </ul>
              <p v-if="!sessions.length" class="dash-empty-small">{{ $t('dashboard.noSessions') }}</p>
            </div>
          </div>
        </li>
      </ul>

      <!-- Danger zone -->
      <div class="danger-zone">
        <button class="dash-btn-danger" @click="openDeleteModal">
          {{ $t('dashboard.deleteAccount') }}
        </button>
        <p class="danger-hint">{{ $t('dashboard.deleteAccountHint') }}</p>
      </div>
    </main>

    <!-- Delete account modal (custom, so the password stays masked) -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="closeDeleteModal">
      <div class="modal-card" role="dialog" aria-modal="true">
        <h3>{{ $t('dashboard.deleteAccount') }}</h3>
        <p class="modal-warning">{{ $t('dashboard.deleteAccountConfirm') }}</p>
        <label class="modal-label" for="delete-password">{{ $t('dashboard.deleteAccountPrompt') }}</label>
        <div class="pw-wrap">
          <input
            id="delete-password"
            v-model="deletePassword"
            :type="showDeletePassword ? 'text' : 'password'"
            class="dash-input pw-input"
            autocomplete="current-password"
            @keyup.enter="confirmRemoveAccount" />
          <button type="button" class="pw-toggle" @click="showDeletePassword = !showDeletePassword">
            {{ showDeletePassword ? $t('auth.hidePw') : $t('auth.showPw') }}
          </button>
        </div>
        <p v-if="deleteError" class="dash-error modal-error">{{ deleteError }}</p>
        <div class="modal-actions">
          <button class="dash-btn-plain" @click="closeDeleteModal">{{ $t('dashboard.cancel') }}</button>
          <button
            class="dash-btn-danger dash-btn-danger-solid"
            :disabled="!deletePassword || busy"
            @click="confirmRemoveAccount">
            {{ $t('dashboard.deleteConfirmCta') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api, authState, isLoggedIn, logout, deleteAccount } from './auth';
import { applySeo } from './i18n/head';
import { localeInfo } from './i18n/locales';

export default {
  data() {
    return {
      patients: [],
      expandedId: null,
      notes: [],
      sessions: [],
      newPatientName: '',
      newNote: '',
      errorMessage: '',
      busy: false,
      loaded: false,
      showDeleteModal: false,
      deletePassword: '',
      showDeletePassword: false,
      deleteError: ''
    };
  },
  computed: {
    email() {
      return authState.email;
    },
    homePath() {
      return this.$localePath('/');
    },
    loginPath() {
      return this.$localePath('/login');
    }
  },
  created() {
    if (!isLoggedIn()) {
      this.$router.replace(this.loginPath);
      return;
    }
    applySeo({
      locale: this.$locale,
      routeBase: 'dashboard',
      title: this.$t('dashboard.metaTitle'),
      description: this.$t('dashboard.metaDescription'),
      noindex: true
    });
    this.loadPatients();
  },
  methods: {
    handleError(error) {
      if (error.response && error.response.status === 401) {
        this.$router.replace(this.loginPath);
        return;
      }
      const serverMessage = error.response && error.response.data && error.response.data.message;
      this.errorMessage = serverMessage || this.$t('auth.genericError');
    },
    async loadPatients() {
      try {
        const { data } = await api.get('/api/patients');
        this.patients = data.patients;
        this.loaded = true;
      } catch (error) {
        this.handleError(error);
      }
    },
    async addPatient() {
      this.busy = true;
      this.errorMessage = '';
      try {
        await api.post('/api/patients', { name: this.newPatientName });
        this.newPatientName = '';
        await this.loadPatients();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.busy = false;
      }
    },
    async deletePatient(patient) {
      if (!window.confirm(this.$t('dashboard.confirmDeletePatient'))) return;
      try {
        await api.delete(`/api/patients/${patient.id}`);
        if (this.expandedId === patient.id) this.expandedId = null;
        await this.loadPatients();
      } catch (error) {
        this.handleError(error);
      }
    },
    async toggleExpand(patientId) {
      if (this.expandedId === patientId) {
        this.expandedId = null;
        return;
      }
      this.expandedId = patientId;
      this.notes = [];
      this.sessions = [];
      this.newNote = '';
      await this.loadDossier(patientId);
    },
    async loadDossier(patientId) {
      try {
        const [notesRes, sessionsRes] = await Promise.all([
          api.get(`/api/patients/${patientId}/notes`),
          api.get(`/api/patients/${patientId}/sessions`)
        ]);
        this.notes = notesRes.data.notes;
        this.sessions = sessionsRes.data.sessions;
      } catch (error) {
        this.handleError(error);
      }
    },
    async addNote(patient) {
      this.busy = true;
      try {
        await api.post(`/api/patients/${patient.id}/notes`, { note: this.newNote });
        this.newNote = '';
        await this.loadDossier(patient.id);
        await this.loadPatients();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.busy = false;
      }
    },
    async deleteNote(patient, note) {
      try {
        await api.delete(`/api/patients/${patient.id}/notes/${note.id}`);
        await this.loadDossier(patient.id);
        await this.loadPatients();
      } catch (error) {
        this.handleError(error);
      }
    },
    async startSession(patient) {
      try {
        const { data } = await api.post(`/api/patients/${patient.id}/sessions`);
        window.open(this.controllerUrl(data.session_id), '_blank');
        await this.loadPatients();
        if (this.expandedId === patient.id) {
          await this.loadDossier(patient.id);
        }
      } catch (error) {
        this.handleError(error);
      }
    },
    controllerUrl(sessionId) {
      return `${this.$localePath('/controller')}?session=${sessionId}`;
    },
    formatDate(timestamp) {
      return new Date(timestamp * 1000).toLocaleString(
        localeInfo(this.$locale).dateLocale,
        { dateStyle: 'medium', timeStyle: 'short' }
      );
    },
    async doLogout() {
      await logout();
      this.$router.push(this.homePath);
    },
    openDeleteModal() {
      this.deletePassword = '';
      this.showDeletePassword = false;
      this.deleteError = '';
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    async confirmRemoveAccount() {
      if (!this.deletePassword || this.busy) return;
      this.busy = true;
      this.deleteError = '';
      try {
        await deleteAccount(this.deletePassword);
        this.$router.push(this.homePath);
      } catch (error) {
        const serverMessage = error.response && error.response.data && error.response.data.message;
        this.deleteError = serverMessage || this.$t('auth.genericError');
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.dash-page {
  min-height: 100vh;
  background: var(--c-bg);
}
.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem clamp(1.5rem, 5vw, 4rem);
  background: #16302c;
}
.dash-brand {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  color: white;
  font-family: var(--ff-heading);
  font-weight: 600;
  text-decoration: none;
}
.dash-brand img {
  border-radius: 8px;
  object-fit: cover;
}
.dash-header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.dash-email {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
}
.dash-logout {
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  padding: 0.3rem 0.9rem;
  font-family: var(--ff-body);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}
.dash-logout:hover {
  color: var(--c-accent-light);
  border-color: var(--c-accent-light);
}
.dash-main {
  max-width: 900px;
  margin: 0 auto;
  padding: clamp(2rem, 5vh, 3.5rem) clamp(1.25rem, 4vw, 2rem) 4rem;
}
.dash-main h1 {
  font-size: clamp(1.7rem, 3vw, 2.2rem);
  color: var(--c-text);
  margin-bottom: 0.4rem;
}
.dash-sub {
  color: var(--c-text-light);
  font-size: 0.95rem;
  margin-bottom: 2rem;
}
.dash-add {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
}
.dash-input {
  flex: 1;
  min-width: 220px;
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--c-border);
  border-radius: 8px;
  font-family: var(--ff-body);
  font-size: 0.95rem;
  background: white;
}
.dash-textarea {
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--c-border);
  border-radius: 8px;
  font-family: var(--ff-body);
  font-size: 0.92rem;
  background: white;
  resize: vertical;
  margin-bottom: 0.6rem;
}
.dash-btn-primary {
  background: var(--c-accent);
  color: #1a2f2c;
  border: none;
  border-radius: 8px;
  padding: 0.7rem 1.4rem;
  font-family: var(--ff-body);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--tr-fast);
}
.dash-btn-primary:hover {
  background: var(--c-accent-light);
}
.dash-btn-small {
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
}
.dash-btn-danger {
  background: transparent;
  color: #b3403a;
  border: 1px solid rgba(179, 64, 58, 0.4);
  border-radius: 8px;
  padding: 0.5rem 0.9rem;
  font-family: var(--ff-body);
  font-size: 0.85rem;
  cursor: pointer;
}
.dash-btn-danger:hover {
  background: rgba(179, 64, 58, 0.08);
}
.dash-error {
  color: #b3403a;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}
.dash-empty {
  color: var(--c-text-light);
  font-size: 0.95rem;
  padding: 1.5rem 0;
}
.dash-empty-small {
  color: var(--c-text-light);
  font-size: 0.85rem;
}
.patient-list {
  list-style: none;
}
.patient-card {
  border: 1px solid var(--c-border);
  border-radius: 12px;
  background: white;
  margin-bottom: 0.9rem;
  overflow: hidden;
}
.patient-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  flex-wrap: wrap;
}
.patient-name {
  background: none;
  border: none;
  font-family: var(--ff-body);
  font-size: 1rem;
  font-weight: 600;
  color: var(--c-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  text-align: left;
}
.patient-caret {
  color: var(--c-accent);
}
.patient-meta {
  color: var(--c-text-light);
  font-weight: 400;
  font-size: 0.82rem;
}
.patient-actions {
  display: flex;
  gap: 0.6rem;
}
.dossier {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.75rem;
  padding: 1.25rem;
  border-top: 1px solid var(--c-border);
  background: var(--c-bg-alt);
}
.dossier-col h3 {
  font-size: 1rem;
  color: var(--c-text);
  margin-bottom: 0.8rem;
}
.note-list, .session-list {
  list-style: none;
  margin-top: 1rem;
}
.note-item {
  background: white;
  border: 1px solid var(--c-border);
  border-radius: 8px;
  padding: 0.7rem 0.85rem;
  margin-bottom: 0.6rem;
}
.note-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.3rem;
}
.note-date {
  font-size: 0.75rem;
  color: var(--c-text-light);
}
.note-delete {
  background: none;
  border: none;
  color: var(--c-text-light);
  cursor: pointer;
  font-size: 0.8rem;
}
.note-delete:hover {
  color: #b3403a;
}
.note-text {
  font-size: 0.9rem;
  color: var(--c-text);
  white-space: pre-wrap;
}
.session-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.88rem;
  color: var(--c-text);
  padding: 0.55rem 0;
  border-bottom: 1px solid var(--c-border);
}
.session-item a {
  color: var(--c-primary);
  font-weight: 600;
}
.danger-zone {
  margin-top: 3.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--c-border);
}
.danger-hint {
  color: var(--c-text-light);
  font-size: 0.8rem;
  margin-top: 0.6rem;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(20, 31, 29, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 100;
}
.modal-card {
  background: var(--c-bg);
  border-radius: 14px;
  padding: 1.75rem;
  width: min(440px, 100%);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.3);
}
.modal-card h3 {
  font-size: 1.2rem;
  color: var(--c-text);
  margin-bottom: 0.6rem;
}
.modal-warning {
  color: #b3403a;
  font-size: 0.88rem;
  line-height: 1.5;
  margin-bottom: 1.1rem;
}
.modal-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--c-text);
  margin-bottom: 0.4rem;
}
.pw-wrap {
  display: flex;
  gap: 0.5rem;
}
.pw-input {
  flex: 1;
  min-width: 0;
}
.pw-toggle {
  background: transparent;
  border: 1px solid var(--c-border);
  border-radius: 8px;
  padding: 0 0.9rem;
  font-family: var(--ff-body);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--c-text-light);
  cursor: pointer;
  white-space: nowrap;
}
.pw-toggle:hover {
  color: var(--c-text);
  border-color: var(--c-text-light);
}
.modal-error {
  margin-top: 0.7rem;
  margin-bottom: 0;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.4rem;
}
.dash-btn-plain {
  background: transparent;
  border: 1px solid var(--c-border);
  border-radius: 8px;
  padding: 0.5rem 1.1rem;
  font-family: var(--ff-body);
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--c-text);
  cursor: pointer;
}
.dash-btn-danger-solid {
  background: #b3403a;
  color: white;
  border: none;
}
.dash-btn-danger-solid:hover {
  background: #9c332e;
}
.dash-btn-danger-solid:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
@media (max-width: 700px) {
  .dossier {
    grid-template-columns: 1fr;
  }
  .patient-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
